import { normalizeAnalyticsAttributionValue } from "@/lib/analytics/attributionValue";
import { isLocalAnalyticsEnvironment } from "@/lib/analytics/environment";
import { logger } from "@/lib/logging/logger";

const UTM_QUERY_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

export type UtmAttribution = {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
};

type UtmQueryKey = (typeof UTM_QUERY_KEYS)[number];

const QUERY_KEY_TO_ATTR_KEY: Record<UtmQueryKey, keyof UtmAttribution> = {
  utm_source: "source",
  utm_medium: "medium",
  utm_campaign: "campaign",
  utm_term: "term",
  utm_content: "content",
};

const ATTR_KEY_TO_QUERY_KEY = Object.fromEntries(
  Object.entries(QUERY_KEY_TO_ATTR_KEY).map(([queryKey, attrKey]) => [attrKey, queryKey]),
) as Record<keyof UtmAttribution, UtmQueryKey>;

const isLocal = isLocalAnalyticsEnvironment();
const log = logger.child("AnalyticsUTM");

// Bridges the query-parser shape and the wire keys used by attribution cookies and the API.
export const toWireUtm = (utm: UtmAttribution): Record<string, string> => {
  const wire: Record<string, string> = {};
  for (const attrKey of Object.keys(utm) as (keyof UtmAttribution)[]) {
    const value = normalizeAnalyticsAttributionValue(utm[attrKey]);
    if (value) {
      wire[ATTR_KEY_TO_QUERY_KEY[attrKey]] = value;
    }
  }
  return wire;
};

const normalizeUtmValues = (params: URLSearchParams): UtmAttribution | undefined => {
  const values: UtmAttribution = {};
  let hasValue = false;

  for (const queryKey of UTM_QUERY_KEYS) {
    const value = normalizeAnalyticsAttributionValue(params.get(queryKey));
    if (value) {
      hasValue = true;
      const attrKey = QUERY_KEY_TO_ATTR_KEY[queryKey];
      values[attrKey] = value;
    }
  }

  return hasValue ? values : undefined;
};

export const extractUtmFromLocation = (locationSearch: string): UtmAttribution | undefined => {
  try {
    const params = new URLSearchParams(locationSearch);
    return normalizeUtmValues(params);
  } catch (error) {
    if (isLocal) {
      log.warn("Failed to parse location search for UTM params", {
        error,
        suppressTelemetry: true,
      });
    }
    return undefined;
  }
};
